import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { useAuthContext } from "@/contexts/AuthContext"
import { useUserEdit } from "@/graphql/hooks/useUserEdit"
import { useCountries, useRegions, useLocations } from "@/graphql/hooks/Locations/useLocations"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { LoadingScreen } from "@/components/ui/loading-spinner"
import { useNavigate } from "react-router-dom"
import { ArrowLeft, InfoIcon, Save } from "lucide-react"
import Layout from "@/components/Layout/Layout"
import ChangePasswordSection from "@/components/profile/ChangePasswordSection"

const profileSchema = z.object({
  name: z.string().optional(),
  firstSurname: z.string().optional(),
  lastSurname: z.string().optional(),
  email: z.string().email("Email inválido"),
  phone: z.string().optional(),
  birthday: z.string().optional(),
  genderId: z.number().optional(),
  categoryId: z.number().optional(),
  address: z.string().min(1, "Dirección requerida"),
  countryId: z.number().min(1, "País requerido"),
  regionId: z.number().min(1, "Región requerida"),
  locationId: z.number().min(1, "Comuna requerida").optional().or(z.literal(0)),
})

type ProfileFormData = z.infer<typeof profileSchema>

const EditProfile = () => {
  const { userInfo, loading: authLoading, isAuthenticated } = useAuthContext()
  const { updateUser, loading: updateLoading } = useUserEdit()
  const navigate = useNavigate()

  const personData = userInfo?.User?.Person
  const isAdmin = personData?.PersonProfiles?.some((profile) => profile.profileId === 3) // Assuming profileId 1 is admin

  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: "",
      firstSurname: "",
      lastSurname: "",
      email: "",
      phone: "",
      birthday: "",
      genderId: 1,
      categoryId: 1,
      address: "",
      countryId: 0,
      regionId: 0,
      locationId: 0,
    },
  })

  const { countries } = useCountries()
  const { regions, loading: loadingRegions } = useRegions(form.getValues("countryId"))
  const { locations, loading: loadingLocations, refetchLocations } = useLocations()

  useEffect(() => {
    if (personData) {
      const countryId = personData.Address?.countryId || 0
      const regionId = personData.Address?.regionId || 0

      form.reset({
        name: personData.name || "",
        firstSurname: personData.firstSurname || "",
        lastSurname: personData.lastSurname || "",
        email: personData.email || "",
        phone: personData.phone || "",
        birthday: personData.birthday ? personData.birthday.split("T")[0] : "",
        genderId: personData.genderId || 1,
        categoryId: personData.categoryId || 1,
        address: personData.Address?.address || "",
        countryId: countryId,
        regionId: regionId,
        locationId: personData.Address?.locationId || 0,
      })

      // Refetch regions when country is set
      if (countryId > 0) {
        // refetchRegions(countryId);
      }

      // Refetch locations when region is set
      if (regionId > 0) {
        refetchLocations(regionId)
      }
    }
  }, [personData, form])

  if (authLoading) {
    return <LoadingScreen message="Cargando datos..." />
  }

  if (!isAuthenticated && !authLoading) {
    navigate("/login")
    return null
  }

  if (!personData) {
    return (
      <Layout>
        <div className="container mx-auto py-8">
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground">No se encontró información del usuario</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    )
  }

  const onSubmit = async (data: ProfileFormData) => {
    try {
      const resolve = await updateUser({
        id: personData.id,
        name: data.name,
        firstSurname: data.firstSurname,
        lastSurname: data.lastSurname,
        email: data.email,
        phone: data.phone,
        birthday: data.birthday,
        genderId: data.genderId,
        categoryId: data.categoryId,
        active: 1,
        address: {
          id: personData.Address?.id,
          name: "Casa",
          address: data.address,
          countryId: Number(data.countryId),
          regionId: Number(data.regionId),
          locationId: Number(data.locationId),
        },
      })
      if (resolve?.data?.UserEdit?.status?.success) {
        navigate("/profile")
      }
    } catch (error) {
      console.error("Error updating profile:", error)
    }
  }

  return (
    <Layout>
      <div className="container mx-auto py-8 space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => navigate("/profile")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver al Perfil
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Editar Perfil</h1>
            <p className="text-muted-foreground">Actualiza tu información personal</p>
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Personal Information */}
            <Card>
              <CardHeader>
                <CardTitle>Información Personal</CardTitle>
                {!isAdmin && (
                  <p className="text-xs text-muted-foreground">
                    <InfoIcon className="inline h-4 w-4 mr-2" />
                    Solo administradores pueden modificar el nombre y apellidos.
                  </p>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nombre</FormLabel>
                        <FormControl>
                          <Input {...field} disabled={!isAdmin} />
                        </FormControl>

                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="firstSurname"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Primer Apellido</FormLabel>
                        <FormControl>
                          <Input {...field} disabled={!isAdmin} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="lastSurname"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Segundo Apellido</FormLabel>
                        <FormControl>
                          <Input {...field} disabled={!isAdmin} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input {...field} type="email" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Teléfono</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="birthday"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Fecha de Nacimiento</FormLabel>
                        <FormControl>
                          <Input {...field} type="date" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Address Information */}
            <Card>
              <CardHeader>
                <CardTitle>Dirección</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="address"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Dirección</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Dirección completa" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="countryId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>País</FormLabel>
                        <Select
                          key={`countries-${countries.length}`}
                          value={field.value ? String(field.value) : ""}
                          onValueChange={(value) => {
                            const countryId = Number(value)
                            // refetchRegions(countryId);
                            field.onChange(countryId)
                            form.setValue("regionId", 0)
                            form.setValue("locationId", 0)
                          }}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Seleccionar país" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {countries.map((country) => (
                              <SelectItem key={country.id} value={String(country.id)}>
                                {country.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="regionId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Región</FormLabel>
                        <Select
                          value={field.value ? String(field.value) : ""}
                          onValueChange={(value) => {
                            const regionId = Number(value)
                            refetchLocations(regionId)
                            field.onChange(regionId)
                            form.setValue("locationId", 0)
                          }}
                          disabled={loadingRegions}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Seleccionar región" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {regions.map((region) => (
                              <SelectItem key={region.id} value={String(region.id)}>
                                {region.fullName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="locationId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Comuna</FormLabel>
                        <Select
                          value={field.value?.toString()}
                          onValueChange={(value) => field.onChange(parseInt(value))}
                          disabled={loadingLocations}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Seleccionar comuna" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {locations.map((location) => (
                              <SelectItem key={location.id} value={location.id.toString()}>
                                {location.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-4">
              <Button type="submit" disabled={updateLoading}>
                {updateLoading ? (
                  <>Guardando...</>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Guardar Cambios
                  </>
                )}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate("/profile")}>
                Cancelar
              </Button>
            </div>
          </form>
        </Form>

        {/* Password Change Section */}
        <ChangePasswordSection userId={personData.id} />
      </div>
    </Layout>
  )
}

export default EditProfile
